// Разбивката на периода по методи на плащане — една тиха лента под кутийките.
//
// Съществува заради едно правило: методите отвъд „в брой" и „с карта"
// („По сметка", персонализираните) не бива да изчезват от екрана. Таблицата
// показва оборота по станции, а тук стои същият оборот, разрязан по това КАК
// е платен.

import { useTranslation } from "react-i18next";

import { fmtEuro } from "../../../../utils/currency";
import type { MethodEntry } from "../../../../utils/paymentMethodsDisplay";

type Props = {
  entries: MethodEntry[];
  /** Оборотът на периода — сборът, който лентата разрязва. */
  total: number;
};

export const StationsMethodSplit = ({ entries, total }: Props) => {
  const { t } = useTranslation();

  // Празен период: лентата би показала списък от нули — методите се предлагат
  // от конфигурацията, не от данните, и това не казва нищо.
  if (!entries.length || total <= 0) return null;

  return (
    <section className="rounded-xl border border-shade-2 bg-surface px-3.5 py-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="font-pos-mono text-[10px] font-bold uppercase leading-none tracking-[0.1em] text-muted">
          {t("reportsPage.stationsSplitTitle")}
        </span>
        <span className="font-pos-mono text-[12.5px] font-bold tabular-nums text-ink">
          {fmtEuro(total)}
        </span>
      </div>

      <div className="mt-2.5 flex h-1.5 overflow-hidden rounded-full bg-fill">
        {entries.map((entry) =>
          entry.pct > 0 ? (
            /* Дялът и цветът на метода се знаят чак по време на изпълнение —
               единственият позволен inline style тук. */
            <span
              key={entry.id}
              style={{ width: `${entry.pct}%`, background: entry.color }}
            />
          ) : null,
        )}
      </div>

      <div className="mt-2.5 flex flex-wrap gap-x-5 gap-y-1.5">
        {entries.map((entry) => (
          <span key={entry.id} className="flex items-center gap-1.5">
            <span
              aria-hidden
              className="size-[7px] shrink-0 rounded-full"
              style={{ background: entry.color }}
            />
            <span className="text-[10.5px] font-bold uppercase leading-[1.2] tracking-[0.04em] text-muted">
              {entry.label}
            </span>
            <span className="font-pos-mono text-[12px] font-bold tabular-nums text-ink">
              {fmtEuro(entry.amount)}
            </span>
            <span className="font-pos-mono text-[11px] tabular-nums text-faint">
              {`${entry.pct.toFixed(0)} %`}
            </span>
          </span>
        ))}
      </div>
    </section>
  );
};
